(ns bp-clients.tv-app.common.components.thumb
  "Shared components across views"
  (:require
   ["react" :refer [createRef]]
   [bp-clients.tv-app.utils.utils :refer [cloudinary-image] :as utils]
   [bp-libs.design-system.components.thumb :refer [thumb]]
   [reagent.core :as r]))

(defn- on-thumb-unfocus
  "This listener was added because when navigating down with
  last carousel focused, the menu was opening.

  The same occured when navigating up from the first carousel.

  For now, there is no situation when we want to focus other section
  but content (tabindex=10) when navigating down from thumb.

  https://github.com/luke-chang/js-spatial-navigation?tab=readme-ov-file#events"
  [^js/Event e]
  (when-let [next-element (some-> e .-detail .-nextElement)]
    (when (and next-element
               (or
                (= "up" (-> e .-detail .-direction))
                (= "down" (-> e .-detail .-direction)))
               (> (-> e .-detail .-nextElement (.getAttribute "tabindex")) 10))
      (utils/stop-event-propagation e))))

(defn safe-thumb
  "Safe thumb is a thumb component wrapper that fixes layout
  when an image fails load.

  In this thumb the focus stays in a wrapper div. This simplifies
  focus on first element on page load.

  focus-down-selector attribute can be used when it is necessary to force
  navigation down to a specific element.

  To know more, read:
  https://github.com/luke-chang/js-spatial-navigation?tab=readme-ov-file#custom-attributes"
  [config]
  (let [image-loaded?           (r/atom false)
        image-url               (:image-url config)
        image-failed?           (r/atom (-> image-url seq not))
        on-image-load           #(reset! image-loaded? true)
        on-image-failed         #(reset! image-failed? true)
        react-ref               (createRef)]
    (r/create-class
     {:component-did-mount (fn [_]
                             (when-let [ref react-ref.current]
                               (.addEventListener ref "sn:willunfocus" on-thumb-unfocus)))
      :component-will-unmount (fn [_]
                                (when-let [ref react-ref.current]
                                  (.removeEventListener ref "sn:willunfocus" on-thumb-unfocus)))
      :reagent-render (fn [_]
                        (let [updated-config (cond-> config
                                               @image-failed? (merge {:hide-image?      true
                                                                      :dark-background? true}))
                              show-image?    (or @image-loaded? (not @image-failed?))
                              image          (if show-image?
                                               [thumb (dissoc updated-config :tab-index :on-click :on-focus)]
                                               [:div {:class "w-full h-full"}
                                                [:div {:class ["w-full h-full dark-linear-gradient rounded-lg"]
                                                       :style (:container config)}
                                                 [thumb {:container       (or (:container config) {:width "1px" :height "1px"})
                                                         :on-image-load   on-image-load
                                                         :image-url       image-url
                                                         :on-image-failed on-image-failed
                                                         :img-class       "border border-transparent"}]]])]
                          [:div {:class ["focusable-thumb h-full rounded-xl outline-none"
                                         (:item-class config)
                                         "box-border border-4 border-transparent focus:border-white"]
                                 :data-sn-down (when (:focus-down-selector config)
                                                 (:focus-down-selector config))
                                 :ref       react-ref
                                 :on-click  (:on-click config)
                                 :on-focus  (:on-focus config)
                                 :tab-index (:tab-index config)}
                           image]))})))

(def aspect-ratio->container
  "We can have fixed sizes, or responsive
  sizes that respect a pre-defined aspect ratio:
  - [aspect-ratio] : returns a fixed size, in px.
    In general, used in carousels.
  - [aspect-ratio]-responsive: without fixed size,
    but respecting aspect ratio. In general, used
    in grids."
  {"16:9"            {:width "21rem" :height "11.8rem"}
   "16:9-responsive" {:aspect-ratio {:w "16" :h "9"}}
   "dvd"             {:width "16.5rem" :height "24rem"}
   "dvd-responsive"  {:aspect-ratio {:w "11" :h "16"}}})

(defn remove-item-events
  "We can't pass function references to the main thread. So we remove them here.
  They aren't needed in the re-frame global state anyway."
  [item]
  (-> item
      (dissoc :on-click)
      (dissoc :on-focus)))

(defn keep-watching
  [{:keys [progress title subtitle on-click slug thumbnail tab-index on-focus] :as item}]
  (let [image-url (cloudinary-image (:image thumbnail)
                                    {:width "320"})] ;; FIXME bug
    ^{:key slug}
    [safe-thumb {:container       (get aspect-ratio->container "16:9")
                 :on-click        (when on-click #(on-click (remove-item-events item)))
                 :on-focus        (when on-focus #(on-focus (remove-item-events item)))
                 :title           title
                 :subtitle        subtitle
                 :original?       true
                 :show-play-icon? true
                 :tab-index       tab-index
                 :show-lock-icon? (:lock_icon item)
                 :image-filter?   true
                 :watch-progress  (-> progress :completion_percentage)
                 :duration        (:human_duration item)
                 :image-url       image-url}]))

(defn carousel-thumb
  [{:keys [thumbnail on-click title subtitle bottom_subtitle progress id tab-index on-focus] :as carousel-item}]
  (let [image-url (cloudinary-image (:image thumbnail)
                                    {:width "320"})]
    ^{:key id}
    [safe-thumb {:container                            (get aspect-ratio->container
                                                            (:aspect_ratio thumbnail))
                 :watch-progress                       (-> progress :completion_percentage)
                 :on-click                             (when on-click #(on-click (remove-item-events carousel-item)))
                 :on-focus                             (when on-focus #(on-focus (remove-item-events carousel-item)))
                 :missing-image-title                  title
                 :show-play-icon?                      (:play_icon carousel-item)
                 :show-lock-icon?                      (:lock_icon carousel-item)
                 :original?                            (:original_icon carousel-item)
                 :missing-image-title-position-center? true
                 :tab-index                            tab-index
                 :image-url                            image-url
                 :image-filter?                        (:play_icon carousel-item)
                 :duration                             (:human_duration carousel-item)
                 :title                                title
                 :subtitle                             subtitle
                 :bottom-subtitle                      bottom_subtitle}]))

(defn highlight-thumb
  "Highlight component always are the first component in a page.
  We must focus in the first item when this component finishes mount.

  This is why we don't want to load with shimmers and in-view listeners.
  Shimmers are not focusable elements.

  Don't need for in-view listeners since it is known that this component
  will always start visible in viewport."
  [{:keys [thumbnail on-click title subtitle bottom_subtitle id
           tab-index on-focus focus-down-selector] :as carousel-item}]
  (let [image-url (cloudinary-image (:image thumbnail)
                                    {:width "320"})]
    ^{:key id}
    [safe-thumb {:container                            (get aspect-ratio->container
                                                            (:aspect_ratio thumbnail))
                 :on-click                             (when on-click #(on-click (remove-item-events carousel-item)))
                 :on-focus                             (when on-focus #(on-focus (remove-item-events carousel-item)))
                 :focus-down-selector                  (when focus-down-selector focus-down-selector)
                 :missing-image-title                  title
                 :show-play-icon?                      (:play_icon carousel-item)
                 :show-lock-icon?                      (:lock_icon carousel-item)
                 :original?                            (:original_icon carousel-item)
                 :tab-index                            tab-index
                 :missing-image-title-position-center? true
                 :image-url                            image-url
                 :image-filter?                        (:play_icon carousel-item)
                 :duration                             (:human_duration carousel-item)
                 :title                                title
                 :full-image?                          true
                 :subtitle                             subtitle
                 :bottom-subtitle                      bottom_subtitle}]))

(defn grid-thumb
  [{:keys [thumbnail on-click title subtitle bottom_subtitle id tab-index on-focus] :as carousel-item}]
  (let [image-url (cloudinary-image (:image thumbnail)
                                    {:width "320"})]
    ^{:key id}
    [safe-thumb {:container                            (get aspect-ratio->container
                                                            (:aspect_ratio thumbnail))
                 :on-click                             (when on-click #(on-click (remove-item-events carousel-item)))
                 :on-focus                             (when on-focus #(on-focus (remove-item-events carousel-item)))
                 :missing-image-title                  title
                 :show-play-icon?                      (:play_icon carousel-item)
                 :show-lock-icon?                      (:lock_icon carousel-item)
                 :original?                            (:original_icon carousel-item)
                 :tab-index                            tab-index
                 :missing-image-title-position-center? true
                 :image-url                            image-url
                 :image-filter?                        (:play_icon carousel-item)
                 :duration                             (:human_duration carousel-item)
                 :title                                title
                 :full-image?                          true
                 :item-class                           "grid-item"
                 :subtitle                             subtitle
                 :bottom-subtitle                      bottom_subtitle}]))
